import type { PayrollPeriodStatus } from '@/api/client'
import { cn } from '@/lib/cn'
import { Check, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const STEPS: PayrollPeriodStatus[] = ['open', 'computed', 'validated', 'paid', 'closed']

// `computing` is a transient sub-state of `open` (calcul en cours) — same
// step index, distinct pulse styling. `cancelled` is a terminal state off
// the happy path and never rendered as a step position.
function stepIndex(status: PayrollPeriodStatus): number {
  if (status === 'computing') return STEPS.indexOf('open')
  return STEPS.indexOf(status)
}

interface Props {
  status: PayrollPeriodStatus
  variant?: 'compact' | 'full'
  className?: string
}

export function PayrollWorkflowStepper({ status, variant = 'compact', className }: Props) {
  const { t } = useTranslation('payroll')

  if (status === 'cancelled') {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive',
          className,
        )}
      >
        <X className="h-3 w-3" aria-hidden />
        {t('period_status.cancelled')}
      </span>
    )
  }

  const current = stepIndex(status)

  if (variant === 'compact') {
    const styles: Record<PayrollPeriodStatus, string> = {
      open: 'bg-muted text-foreground',
      computing: 'bg-warning/15 text-warning animate-pulse',
      computed: 'bg-primary-muted text-primary',
      validated: 'bg-success/15 text-success',
      paid: 'bg-accent text-accent-foreground',
      closed: 'bg-muted text-muted-foreground',
      cancelled: 'bg-destructive/10 text-destructive line-through',
    }
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
          styles[status],
          className,
        )}
      >
        {t(`period_status.${status}`, { defaultValue: status })}
      </span>
    )
  }

  return (
    <ol className={cn('flex items-center gap-1.5', className)}>
      {STEPS.map((step, idx) => {
        const done = idx < current || (idx === current && status !== 'computing')
        const active = idx === current
        return (
          <li key={step} className="flex flex-1 items-center gap-1.5 last:flex-none">
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold transition-colors duration-200',
                  done && 'border-success bg-success text-white',
                  active &&
                    status === 'computing' &&
                    'border-warning bg-warning/15 text-warning animate-pulse',
                  !done &&
                    !(active && status === 'computing') &&
                    'border-border bg-muted text-muted-foreground',
                )}
              >
                {done ? <Check className="h-3.5 w-3.5" aria-hidden /> : idx + 1}
              </div>
              <span
                className={cn(
                  'whitespace-nowrap text-[11px] font-medium',
                  done || active ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {t(`period_status.${step}`)}
              </span>
            </div>
            {idx < STEPS.length - 1 ? (
              <div
                className={cn(
                  'mb-4 h-0.5 flex-1 rounded-full transition-colors duration-200',
                  idx < current ? 'bg-success' : 'bg-border',
                )}
                aria-hidden
              />
            ) : null}
          </li>
        )
      })}
    </ol>
  )
}
